/**
 * Animation utilities for BaseModal
 */

/**
 * Trigger haptic feedback (vibration)
 * @param {Object} modal - Modal instance
 * @param {string} type - Haptic type: 'light', 'medium', 'heavy', 'success', 'error'
 */
export function triggerHaptic(modal, type = 'light') {
    if (!modal.config.hapticFeedback) return;

    // Check if Vibration API is supported
    if (!navigator.vibrate) return;

    const patterns = {
        light  :10,
        medium :20,
        heavy  :30,
        success:[10, 50, 10],
        error  :[20, 100, 20, 100, 20]
    };

    const pattern = patterns[type] || patterns.light;
    navigator.vibrate(pattern);
}

/**
 * Wait for animation or transition to complete
 * @param {Object} modal - Modal instance
 * @param {HTMLElement} element - Element to watch
 * @param {Function} callback - Callback to execute after animation/transition
 * @param {number} maxWait - Maximum time to wait (fallback)
 */
export function waitForAnimation(modal, element, callback, maxWait = 600) {
    const events = ['animationend', 'transitionend'];
    let fired    = false;

    if (modal.config.debugMode) {
        console.log('🎬 waitForAnimation: Start waiting for animation', {
            element,
            maxWait,
            classList :element.className,
            ariaHidden:element.getAttribute('aria-hidden')
        });
    }

    const handler = (e) => {
        if (modal.config.debugMode) {
            console.log('🎬 waitForAnimation: Event fired', {
                eventType           :e.type,
                target              :e.target,
                element,
                isTargetElement     :e.target === element,
                isContainerOrOverlay:e.target.classList.contains('bm-modal__container') || e.target.classList.contains('bm-modal__overlay'),
                fired,
                animationName       :e.animationName,
                propertyName        :e.propertyName
            });
        }

        // Trigger if event is from modal itself or its direct children (container/overlay)
        // This handles bubbled animation events from child elements
        const isValidTarget = e.target === element ||
            e.target.classList.contains('bm-modal__container') ||
            e.target.classList.contains('bm-modal__overlay');

        if (isValidTarget && !fired) {
            fired = true;
            events.forEach(event => element.removeEventListener(event, handler));

            if (modal.config.debugMode) {
                console.log('✅ waitForAnimation: Animation complete, executing callback', {
                    eventType    :e.type,
                    target       :e.target.className,
                    animationName:e.animationName,
                    propertyName :e.propertyName
                });
            }

            callback();
        }
    };

    // Listen for both animation and transition end
    events.forEach(event => element.addEventListener(event, handler));

    // Fallback timeout in case animation/transition doesn't fire
    setTimeout(() => {
        if (!fired) {
            fired = true;
            events.forEach(event => element.removeEventListener(event, handler));

            if (modal.config.debugMode) {
                console.warn('⏱️ waitForAnimation: Timeout reached, forcing callback', {
                    maxWait,
                    element,
                    classList:element.className
                });
            }

            callback();
        }
    }, maxWait);
}

/**
 * Toggle fullscreen mode for modal
 * @param {Object} modal - Modal instance
 * @returns {Promise} Promise that resolves when fullscreen state changes
 */
export async function toggleFullscreen(modal) {
    try {
        if (!document.fullscreenElement) {
            // Enter fullscreen
            await modal.modal.requestFullscreen();
            modal.modal.classList.add('bm-modal--fullscreen');
            modal.emit('fullscreenEnter', {modal:modal.modal});

            if (modal.config.debugMode) {
                console.log('BaseModal: Entered fullscreen');
            }
        } else {
            // Exit fullscreen
            await document.exitFullscreen();
            modal.modal.classList.remove('bm-modal--fullscreen');
            modal.emit('fullscreenExit', {modal:modal.modal});

            if (modal.config.debugMode) {
                console.log('BaseModal: Exited fullscreen');
            }
        }
    } catch (error) {
        if (modal.config.debugMode) {
            console.error('BaseModal: Fullscreen error', error);
        }
    }
}
